import { retry, type BaseQueryApi, type BaseQueryArg, type BaseQueryFn } from '@reduxjs/toolkit/query/react';

/**
 * Le UNICHE `extraOptions` che un endpoint di un client con questa baseQuery puo' dichiarare. Sono nel TIPO perche' il
 * `extraOptions` di una `fetchBaseQuery` e' `{}`, e con `{}` un refuso (`maxRetires: 0`) o un valore sbagliato
 * (`idempotente: 'true'`) compilano e non fanno niente, in silenzio.
 */
export type OpzioniRitentativo = {
  /** Spegne il ritentativo (0) — mai lo alza: il tetto e' UNO per gli errori restituiti. */
  maxRetries?: number;
  /** L'eccezione dichiarata di una scrittura idempotente: v. il commento qui sotto. Solo `true`. */
  idempotente?: true;
};

/**
 * Il ritentativo di `qbotApi`, `qduelsApi`, `qchessApi`, `qclansApi` e `qriotsApi`: UNA volta, solo per le LETTURE,
 * solo se l'errore e' transitorio. (Nato per `qbotApi` in #513; gli altri quattro client l'hanno adottato dopo.)
 *
 * ⛔ Prima era `retry(…, { maxRetries: 1 })`: il `retry` di RTK ritenta OGNI errore — 4xx compresi, e anche le
 * MUTATION (`retryCondition` di default = `attempt <= maxRetries`). Un `saveDeck` o un `coach*` andati a buon
 * fine sul server ma con la risposta persa venivano RIPETUTI: un mazzo duplicato, una seconda quota AI consumata.
 * (Trovato in revisione di #512, che l'aveva aggirato per la sola PATCH del consenso.)
 *
 *  · una SCRITTURA non si ritenta MAI, qualunque sia l'errore — salvo l'ECCEZIONE DICHIARATA qui sotto —: la si esclude PRIMA del `retry`, non con la sua
 *    `retryCondition`. La `retryCondition` di RTK vale solo per gli errori restituiti dalla baseQuery; un errore
 *    LANCIATO — un'eccezione dentro la baseQuery, fuori dal suo try: `localStorage` in `getQbotAccessToken`,
 *    `new Request(…)`, `JSON.stringify` del body — passa da un altro ramo, che rispetta solo `maxRetries`.
 *    Escluderla a monte tiene un solo meccanismo per tutti e due i casi.
 *    ⚠️ Non e' il token mancante: `getQbotAccessToken` in quel caso RESTITUISCE `null` (`logto.ts:1250-1263`) e la
 *    richiesta parte senza `Authorization`, cioe' un 401 restituito, che questa condizione non ritenta.
 *    «Scrittura» = una mutation (`api.type`) **oppure** una richiesta con un `method` diverso da GET, anche se
 *    dichiarata come query: oggi nei cinque client i due criteri coincidono, il secondo protegge chi scrivera' una query POST;
 *  · ⚠️ L'ECCEZIONE DICHIARATA: un endpoint puo' dichiararsi idempotente con `extraOptions: { idempotente: true }`, e allora
 *    la sua scrittura passa dal ritentativo delle letture (UNA volta, solo su `FETCH_ERROR`, `TIMEOUT_ERROR` o status
 *    >= 500 con corpo JSON; un 4xx mai). «Idempotente» qui vuol dire che RIPETERLA NON CREA E NON SPENDE NIENTE: se anche
 *    la prima chiamata fosse arrivata, la seconda non aggiunge un doppione ne' consuma una quota. ⛔ NON vuol dire che
 *    «un arrivo in ritardo sia innocuo»: una seconda chiamata che arriva DOPO un'altra scrittura dello stesso giocatore
 *    puo' disfarla (il ritentativo parte dopo 0,24-0,84 s dalla prima, e la prima non ha un tetto di durata). Per questo
 *    ogni dichiarazione porta scritto, ACCANTO all'endpoint, cosa puo' disfare un secondo tentativo tardivo.
 *    Non e' la risposta del server: e' una dichiarazione di chi scrive l'endpoint, e per questo l'elenco di chi la fa e'
 *    ENUMERATO a mano nel banco (`tests/ritentativi-altri-client.test.tsx`), che lo confronta col SORGENTE (le
 *    occorrenze della PAROLA `idempotente` in `src/services/`, ognuna nella forma `idempotente: true` dentro l'endpoint
 *    elencato) e col comportamento a runtime di TUTTE le mutation dei cinque client: una dichiarazione nuova fa rosso
 *    finche' non entra nell'elenco. Non vede una chiave calcolata ne' un `extraOptions` importato da fuori `src/services/`.
 *    Oggi TRE, tutte in `qduelsApi`: `queueLeave`, `checkInQueue`, `checkInTournament` (ragione e costo di ciascuna
 *    sull'endpoint). Senza la dichiarazione una scrittura non si ritenta;
 *  · una LETTURA si ritenta una volta sola, su `FETCH_ERROR`, `TIMEOUT_ERROR` o status >= 500 (la condizione che
 *    avevano gia' `qduelsApi`, `qchessApi` e `qclansApi`); un 4xx e' deterministico e non si ritenta mai: ritentare ritarda soltanto l'errore.
 *    ⚠️ Un 5xx con corpo NON JSON (la pagina d'errore HTML di un proxy) arriva come `PARSING_ERROR`, non come un numero: NON si
 *    ritenta, ne' una lettura ne' una scrittura dichiarata. Un errore LANCIATO in una lettura fa DUE tentativi (`maxRetries: 1`);
 *    Un endpoint che dichiara `extraOptions: { maxRetries: 0 }` (l'idioma di #512) NON si ritenta neppure su un 5xx.
 *
 * I tempi di attesa fra i tentativi sono quelli di default di RTK: non si toccano qui.
 */
export function ritentabile(
  error: unknown,
  _args: unknown,
  { attempt, extraOptions }: { attempt: number; extraOptions?: { maxRetries?: number } },
): boolean {
  // `attempt` e' 1 dopo il primo fallimento: 1 => una seconda prova, 2 => basta. Il tetto e' UNO, e un endpoint
  // puo' solo abbassarlo (`maxRetries: 0` = nessun ritentativo): la nostra regola non si alza da un endpoint.
  // ⚠️ Vale per gli errori RESTITUITI, che decide questa funzione. Per gli errori LANCIATI (fuori dalla `fetch`) decide
  // solo `maxRetries` di RTK, e li' un endpoint puo' ALZARLO: `extraOptions: { maxRetries: 5 }` = 6 tentativi. Nessun
  // endpoint lo fa oggi (e un errore lanciato non lascia partire nessuna richiesta: il costo e' l'attesa).
  const tetto = typeof extraOptions?.maxRetries === 'number' ? Math.min(1, extraOptions.maxRetries) : 1;
  if (attempt > tetto) return false;
  const status = (error as { status?: number | string } | undefined)?.status;
  if (status === 'FETCH_ERROR' || status === 'TIMEOUT_ERROR') return true;
  return typeof status === 'number' && status >= 500;
}

/** Una richiesta con un `method` che non e' GET (assente = GET). Un `method` che non e' una stringa e' una scrittura. */
function metodoNonGet(args: unknown): boolean {
  if (typeof args !== 'object' || args === null) return false;
  const metodo = (args as { method?: unknown }).method;
  if (metodo === undefined) return false;
  return !(typeof metodo === 'string' && metodo.toUpperCase() === 'GET');
}

/** L'endpoint ha dichiarato `extraOptions: { idempotente: true }` (solo `true`, mai un valore «truthy»). */
function dichiaraIdempotente(extraOptions: unknown): boolean {
  if (typeof extraOptions !== 'object' || extraOptions === null) return false;
  return (extraOptions as { idempotente?: unknown }).idempotente === true;
}

/** La stessa baseQuery, con le `extraOptions` di un endpoint ristrette a `OpzioniRitentativo` (argomenti e risultato invariati). */
export type BaseQueryConOpzioni<BQ extends BaseQueryFn> = (
  args: BaseQueryArg<BQ>,
  api: BaseQueryApi,
  extraOptions: OpzioniRitentativo,
) => ReturnType<BQ>;

export function ritentaSoloLeLetture<BQ extends BaseQueryFn>(baseQuery: BQ): BaseQueryConOpzioni<BQ> {
  // `maxRetries` e `retryCondition` insieme non sono nel tipo di RTK (o l'uno o l'altra), ma a runtime valgono
  // entrambi: la condizione decide per gli errori restituiti, `maxRetries: 1` limita gli errori LANCIATI (senza
  // di lui varrebbe il default 5, con backoff crescente).
  const conRitentativo = retry(baseQuery, {
    maxRetries: 1,
    retryCondition: ritentabile,
  } as unknown as Parameters<typeof retry>[1]);
  return ((args: BaseQueryArg<BQ>, api: BaseQueryApi, extraOptions: OpzioniRitentativo) =>
    (api.type === 'mutation' || metodoNonGet(args)) && !dichiaraIdempotente(extraOptions)
      ? baseQuery(args, api, extraOptions)
      : conRitentativo(args, api, extraOptions)) as unknown as BaseQueryConOpzioni<BQ>;
}
